
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { useAuth } from '@/components/AuthProvider';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';

// Define subscription tier limits
export interface SubscriptionLimits {
  clients: number;
  projects: number;
  invoices: number;
  timeEntries: number;
  storage: number; // in MB
}

// Define the current usage metrics
export interface UsageMetrics {
  clients: number;
  projects: number;
  invoices: number;
  timeEntries: number;
  storage: number; // in MB
}

// Define subscription tiers with their limits
const SUBSCRIPTION_TIERS: Record<string, SubscriptionLimits> = {
  free: {
    clients: 1,        
    projects: 1,        
    invoices: 1,        
    timeEntries: 5,    
    storage: 10
  },
  essentials: {
    clients: 20,        
    projects: 20,       
    invoices: 20,       
    timeEntries: -1,    // Unlimited (-1)
    storage: 100
  },
  pro: {
    clients: -1,        // unlimited (-1)
    projects: -1,       // unlimited
    invoices: -1,       // unlimited
    timeEntries: -1,    // unlimited
    storage: 1000
  }
};

// Add a fallback tier with moderate limits in case of uncertainty
const FALLBACK_TIER: SubscriptionLimits = {
  clients: 10,
  projects: 10,
  invoices: 10,
  timeEntries: 50,
  storage: 50
};

interface SubscriptionLimitsContextType {
  userTier: string;
  limits: SubscriptionLimits;
  usage: UsageMetrics;
  isNearLimit: (resourceType: keyof UsageMetrics) => boolean;
  isAtLimit: (resourceType: keyof UsageMetrics) => boolean;
  checkAndWarnLimits: (resourceType: keyof UsageMetrics) => boolean;
  getResourceUsagePercentage: (resourceType: keyof UsageMetrics) => number;
  refreshUsage: () => Promise<void>;
  subscriptionSyncStatus: 'synced' | 'syncing' | 'error' | 'not-found';
}

const SubscriptionLimitsContext = createContext<SubscriptionLimitsContextType | undefined>(undefined);

export const useSubscriptionLimits = () => {
  const context = useContext(SubscriptionLimitsContext);
  if (!context) {
    throw new Error('useSubscriptionLimits must be used within a SubscriptionLimitsProvider');
  }
  return context;
};

export const SubscriptionLimitsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [userTier, setUserTier] = useState<string>('free');
  const [limits, setLimits] = useState<SubscriptionLimits>(SUBSCRIPTION_TIERS.free);
  const [usage, setUsage] = useState<UsageMetrics>({
    clients: 0,
    projects: 0,
    invoices: 0,
    timeEntries: 0,
    storage: 0
  });
  const [retryCount, setRetryCount] = useState(0);
  const [syncStatus, setSyncStatus] = useState<'synced' | 'syncing' | 'error' | 'not-found'>('synced');
  const [cacheExpiry, setCacheExpiry] = useState<number | null>(null);
  
  // Fetch user's subscription information
  useEffect(() => {
    if (user) {
      console.log('SubscriptionLimitsProvider: Initializing for user:', user.id);
      setSyncStatus('syncing');
      fetchSubscriptionInfo();
      fetchUsageMetrics();
      
      // Set up cache expiry time - 5 minutes from now
      setCacheExpiry(Date.now() + (5 * 60 * 1000));
      
      // Listen for subscription changes via Realtime
      const channel = supabase
        .channel('user-subscription')
        .on('postgres_changes', {
          event: '*',
          schema: 'public',
          table: 'subscriptions',
          filter: `user_id=eq.${user.id}`
        }, () => {
          console.log('Subscription changed, refreshing data');
          fetchSubscriptionInfo();
          fetchUsageMetrics();
          setCacheExpiry(Date.now() + (5 * 60 * 1000));
        })
        .subscribe();
      
      return () => {
        supabase.removeChannel(channel);
      };
    }
  }, [user]);
  
  // Check if cache has expired
  useEffect(() => {
    if (!user || !cacheExpiry) return;
    
    // If cache has expired, refresh data
    if (Date.now() > cacheExpiry) {
      console.log('Subscription cache expired, refreshing data');
      fetchSubscriptionInfo();
      fetchUsageMetrics();
      setCacheExpiry(Date.now() + (5 * 60 * 1000));
    }
    
    // Set up regular check for cache expiry
    const intervalId = setInterval(() => {
      if (cacheExpiry && Date.now() > cacheExpiry) {
        console.log('Subscription cache expired during interval check, refreshing data');
        fetchSubscriptionInfo();
        fetchUsageMetrics();
        setCacheExpiry(Date.now() + (5 * 60 * 1000));
      }
    }, 60 * 1000); // Check every minute
    
    return () => clearInterval(intervalId);
  }, [user, cacheExpiry]);

  const fetchSubscriptionInfo = async () => {
    if (!user) return;
    
    try {
      console.log('Fetching subscription info for user:', user.id);
      setSyncStatus('syncing');
      
      // Get current session and refresh if expired
      let { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session) {
        console.error('No active session found, attempting refresh...', sessionError);
        
        // Try to refresh the session
        const { data: { session: refreshedSession }, error: refreshError } = await supabase.auth.refreshSession();
        
        if (refreshError || !refreshedSession) {
          console.error('Session refresh failed:', refreshError);
          setSyncStatus('error');
          setUserTier('free');
          setLimits(FALLBACK_TIER);
          return;
        }
        
        session = refreshedSession;
      }
      
      // Use the simplified get-subscription function with explicit auth
      const { data, error } = await supabase.functions.invoke('get-subscription', {
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });

      if (error) {
        console.error('Error fetching subscription:', error);
        
        // If 401, try refreshing session once
        if (error.message?.includes('401') || error.message?.includes('User not found')) {
          console.log('Attempting to refresh session due to 401 error...');
          const { data: { session: refreshedSession }, error: refreshError } = await supabase.auth.refreshSession();
          
          if (!refreshError && refreshedSession) {
            // Retry with refreshed token
            const { data: retryData, error: retryError } = await supabase.functions.invoke('get-subscription', {
              headers: {
                Authorization: `Bearer ${refreshedSession.access_token}`
              }
            });
            
            if (!retryError && retryData) {
              console.log('Subscription data received after refresh:', retryData);
              
              if (retryData && retryData.subscribed) {
                const tier = retryData.subscription_tier.toLowerCase();
                console.log(`Setting user tier to: ${tier}`);
                setUserTier(tier);
                setLimits(SUBSCRIPTION_TIERS[tier] || SUBSCRIPTION_TIERS.free);
                setSyncStatus('synced');
                setRetryCount(0);
                return;
              } else {
                console.log('No active subscription found after refresh');
                setUserTier('free');
                setLimits(SUBSCRIPTION_TIERS.free);
                setSyncStatus('synced');
                setRetryCount(0);
                return;
              }
            }
          }
        }
        
        setSyncStatus('error');
        // Use fallback tier on error
        setUserTier('free');
        setLimits(FALLBACK_TIER);
        return;
      }

      console.log('Subscription data received:', data);
      
      if (data && data.subscribed) {
        const tier = data.subscription_tier.toLowerCase();
        console.log(`Setting user tier to: ${tier}`);
        setUserTier(tier);
        setLimits(SUBSCRIPTION_TIERS[tier] || SUBSCRIPTION_TIERS.free);
        setSyncStatus('synced');
        setRetryCount(0);
      } else {
        console.log('No active subscription found');
        setUserTier('free');
        setLimits(SUBSCRIPTION_TIERS.free);
        setSyncStatus('synced');
        setRetryCount(0);
      }
    } catch (error) {
      console.error('Error fetching subscription info:', error);
      setSyncStatus('error');
      
      // Use fallback tier instead of forcing to free tier
      setUserTier('free');
      setLimits(FALLBACK_TIER);
      
      // Retry logic
      if (retryCount < 2) {
        console.log(`Retry #${retryCount + 1} to fetch subscription`);
        setRetryCount(prev => prev + 1);
      }
    }
  };

  const fetchUsageMetrics = async () => {
    if (!user) return;

    try {
      console.log('Fetching usage metrics for user:', user.id);
      
      // Get clients count
      const { count: clientsCount, error: clientsError } = await supabase
        .from('clients')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id);

      // Get projects count
      const { count: projectsCount, error: projectsError } = await supabase
        .from('projects')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id);

      // Get invoices count
      const { count: invoicesCount, error: invoicesError } = await supabase
        .from('invoices')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id);

      // Get time entries count
      const { count: timeEntriesCount, error: timeEntriesError } = await supabase
        .from('time_entries')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', user.id);

      // For storage, we'd need to calculate based on file sizes, but for this example
      // we'll just use a fixed value or implement a simplified calculation
      const storageUsed = 0; // This would need a real implementation based on your storage system

      if (clientsError || projectsError || invoicesError || timeEntriesError) {
        console.error('Error fetching usage metrics:', { 
          clientsError, projectsError, invoicesError, timeEntriesError 
        });
        return;
      }
      
      console.log('Usage metrics:', {
        clients: clientsCount || 0,
        projects: projectsCount || 0,
        invoices: invoicesCount || 0,
        timeEntries: timeEntriesCount || 0,
        storage: storageUsed
      });

      setUsage({
        clients: clientsCount || 0,
        projects: projectsCount || 0,
        invoices: invoicesCount || 0,
        timeEntries: timeEntriesCount || 0,
        storage: storageUsed
      });
    } catch (error) {
      console.error('Error fetching usage metrics:', error);
    }
  };

  const refreshUsage = async () => {
    console.log('Refreshing subscription and usage data');
    setSyncStatus('syncing');
    await fetchSubscriptionInfo();
    await fetchUsageMetrics();
    // Reset cache expiry
    setCacheExpiry(Date.now() + (5 * 60 * 1000));
  };

  const isNearLimit = (resourceType: keyof UsageMetrics): boolean => {
    const limit = limits[resourceType];
    const currentUsage = usage[resourceType];
    
    // If the limit is -1 (unlimited), never show warning
    if (limit === -1) return false;
    
    return currentUsage >= limit * 0.8 && currentUsage < limit;
  };

  const isAtLimit = (resourceType: keyof UsageMetrics): boolean => {
    const limit = limits[resourceType];
    const currentUsage = usage[resourceType];
    
    // If the limit is -1 (unlimited), never at limit
    if (limit === -1) return false;
    
    return currentUsage >= limit;
  };

  const getResourceUsagePercentage = (resourceType: keyof UsageMetrics): number => {
    const limit = limits[resourceType];
    const currentUsage = usage[resourceType];
    
    // If the limit is -1 (unlimited), show a moderate level
    if (limit === -1) return Math.min((currentUsage / 100) * 10, 50);
    
    return Math.min((currentUsage / limit) * 100, 100);
  };

  const checkAndWarnLimits = (resourceType: keyof UsageMetrics): boolean => {
    if (isAtLimit(resourceType)) {
      toast.error(
        `You've reached your ${userTier} plan limit for ${resourceType}`,
        {
          description: "Upgrade your plan to continue adding more.",
          action: {
            label: 'Upgrade',
            onClick: () => navigate('/subscription-plans')
          },
          duration: 10000,
          id: `limit-reached-${resourceType}`
        }
      );
      return true;
    } else if (isNearLimit(resourceType)) {
      toast.warning(
        `You're approaching your ${userTier} plan limit for ${resourceType}`,
        {
          description: "Consider upgrading your plan soon.",
          action: {
            label: 'View Plans',
            onClick: () => navigate('/subscription-plans')
          },
          duration: 8000,
          id: `near-limit-${resourceType}`
        }
      );
    }
    return false;
  };

  return (
    <SubscriptionLimitsContext.Provider
      value={{
        userTier,
        limits,
        usage,
        isNearLimit,
        isAtLimit,
        checkAndWarnLimits,
        getResourceUsagePercentage,
        refreshUsage,
        subscriptionSyncStatus: syncStatus
      }}
    >
      {children}
    </SubscriptionLimitsContext.Provider>
  );
};
